/**
 * Automations & Executions Types
 *
 * Type definitions for the Automations feature (Task 2564).
 * Aligned with backend API responses from /v1/automations endpoints.
 *
 * @see Task 2564 Section 10 for API response shapes
 */

// ── Automation Status & Trigger Types ───────────────────────────────────────

export type AutomationStatus = 'active' | 'paused' | 'draft' | 'failing' | 'deprecated'
export type AutomationTriggerType = 'event' | 'cron' | 'webhook' | 'manual'

// ── Automation Step ─────────────────────────────────────────────────────────

/**
 * Single step in an automation graph.
 */
export interface AutomationStep {
  index: number
  name: string
  type: string // Step kind: llm-call, http, branch, transform, etc.
  config: Record<string, unknown>
}

// ── Automation Trigger ──────────────────────────────────────────────────────

/**
 * Trigger configuration for an automation.
 */
export interface AutomationTrigger {
  type: AutomationTriggerType
  config: Record<string, unknown>
}

// ── Automation ──────────────────────────────────────────────────────────────

/**
 * Automation entity matching the backend Automation schema.
 */
export interface Automation {
  id: string
  name: string
  description?: string
  status: AutomationStatus
  trigger: AutomationTrigger
  steps: AutomationStep[]
  inputSchema?: Record<string, unknown> // JSON Schema for parameter form
  estimatedRuntimeMs?: number
  lastRunAt?: string // ISO timestamp
  successRate7d?: number // 0..1
  createdAt: string
  updatedAt: string
  // Deprecation state (Task 3121)
  deprecated?: boolean
  deprecatedReason?: string
  deprecatedAt?: string // ISO timestamp
  // Summariser fields
  summariserHint?: string
  summariseExecution?: boolean
  // Raw backend fields preserved for PATCH diff
  _raw?: Record<string, unknown>
}

/**
 * Automation list response from GET /v1/automations.
 */
export interface AutomationListResponse {
  data: Automation[]
  has_more: boolean
  object: 'list'
}

/**
 * Automation update request for PATCH /v1/automations/{id}.
 * Uses nullable pointer fields — only set fields are updated.
 */
export interface AutomationUpdateRequest {
  name?: string
  description?: string
  /** Mapped to the backend's `enabled` boolean by `mapUpdateToBackend`. */
  status?: AutomationStatus
  trigger?: AutomationTrigger
  /**
   * Steps are sent to the backend verbatim, so they must already be in the
   * backend's step shape (`id`/`type`/`config`/`next_step`), not the flattened
   * display shape used by `Automation.steps`.
   */
  steps?: BackendAutomationStep[]
  inputSchema?: Record<string, unknown>
  deprecated?: boolean
  deprecatedReason?: string
  summariserHint?: string
  summariseExecution?: boolean
  conditions?: Record<string, string>
  priority?: number
  triggerEventType?: string
  triggerSchedule?: string
  triggerSource?: string
  entryStepId?: string
}

/**
 * Backend-shaped automation step for create/update requests.
 */
export interface BackendAutomationStep {
  id: string
  name?: string
  description?: string
  type: string
  config: Record<string, unknown>
  branches?: Array<{ match?: string; default?: boolean; goto: string }>
  on_failure?: string
  next_step?: string
  requires_approval?: boolean
}

/**
 * Create automation request for POST /v1/automations.
 */
export interface CreateAutomationRequest {
  name: string
  description?: string
  trigger_event_type?: string
  trigger_schedule?: string
  trigger_source?: string
  steps: BackendAutomationStep[]
  entry_step_id: string
  origin_service?: string
  priority?: number
  enabled?: boolean
  conditions?: Record<string, string>
  facet_fields?: Record<string, string>
  summarise_execution?: boolean
  summariser_hint?: string
}

// ── Execution Status Types ──────────────────────────────────────────────────

export type ExecutionStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'aborted'
  | 'awaiting_approval'

export type ExecutionStepStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped'

// ── Execution Step ──────────────────────────────────────────────────────────

/**
 * Error details for a failed execution step.
 */
export interface ExecutionStepError {
  message: string
  stack?: string
}

/**
 * Single step within an execution.
 */
export interface ExecutionStep {
  index: number
  name: string
  status: ExecutionStepStatus
  startedAt?: string
  completedAt?: string
  durationMs?: number
  cost?: number
  input?: unknown
  output?: unknown
  error?: ExecutionStepError
}

/**
 * Inspect a step's output JSON and return a `session_id` if present.
 *
 * Agent-invocation steps (e.g. `step_invoke`) emit an output object that
 * includes the `session_id` of the agent session they kicked off. This helper
 * narrows the unknown output payload and returns the session id, so UI code
 * can render a "View Session" affordance without doing its own type guarding.
 *
 * Returns null if the output is missing, isn't an object, or has no
 * non-empty `session_id` string.
 */
export function extractStepSessionId(output: unknown): string | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) {
    return null
  }
  const candidate = (output as Record<string, unknown>).session_id
  if (typeof candidate !== 'string' || candidate.length === 0) {
    return null
  }
  return candidate
}

/**
 * Inspect a step's output JSON and return the agent session `user_id` if
 * present.
 *
 * The invoke_agent step (`services/operations/.../invoke_agent_step.go`)
 * stashes the user_id it passed to the ADK runner alongside `session_id` in
 * its output map.  ADK's session storage is keyed on
 * `(app_name, user_id, session_id)`, so without the `user_id` partition key
 * the `/history` endpoint has to fall back to a 7-candidate probe + table
 * scan — the source of the ~12s "View session" stall observed on the Runs
 * tab.  Surfacing the user_id here lets the modal pass it through and hit
 * the direct PK lookup.
 *
 * Returns null if the output is missing, isn't an object, or has no
 * non-empty `user_id` string.
 */
export function extractStepSessionUserId(output: unknown): string | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) {
    return null
  }
  const candidate = (output as Record<string, unknown>).user_id
  if (typeof candidate !== 'string' || candidate.length === 0) {
    return null
  }
  return candidate
}

/**
 * Inspect a step's input JSON and return the `agent_id` if present.
 *
 * Agent-invocation steps (e.g. `step_invoke`) accept an input object that
 * includes the `agent_id` of the agent being invoked. Surfacing this lets
 * the session modal subscribe to the live stream immediately — without
 * waiting for the slow `resolveSession` round-trip — since the agentId is
 * required by the SSE watch-stream endpoint.
 *
 * Returns null if the input is missing, isn't an object, or has no non-empty
 * `agent_id` string.
 */
export function extractStepAgentId(input: unknown): string | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return null
  }
  const candidate = (input as Record<string, unknown>).agent_id
  if (typeof candidate !== 'string' || candidate.length === 0) {
    return null
  }
  return candidate
}

// ── Execution ───────────────────────────────────────────────────────────────

/**
 * Execution entity from GET /v1/executions/{id}.
 */
export interface Execution {
  id: string
  automationId: string
  status: ExecutionStatus
  /**
   * ISO timestamp when the execution started.
   *
   * Optional because the backend ``AutomationExecution`` domain object
   * (``services/operations/backend/internal/core/domain/automation_execution.go``)
   * currently has no ``started_at`` JSON field — it only embeds
   * ``APIResource.Created/Updated``.  Until the backend exposes
   * ``started_at`` (or the frontend api layer maps ``created`` →
   * ``startedAt``), every Execution returned from
   * ``/v1/executions`` will arrive with this field ``undefined``.
   * Consumers must guard against ``undefined`` before constructing a
   * ``Date`` — ``new Date(undefined)`` produces an Invalid Date that
   * crashes ``date-fns`` with ``RangeError: Invalid time value``.
   */
  startedAt?: string
  /**
   * ISO timestamp the execution entered the operator queue (re-stamped each
   * time it parks for review).
   *
   * Mapped from the backend ``AutomationExecution.queued_at`` (a Unix int64,
   * ``json:"queued_at,omitempty"``) via the api-layer ``unixToIso`` helper, so
   * — like ``startedAt`` — it is an ISO-8601 string here, not a Unix number.
   * Optional/absent when the execution never queued (``queued_at`` omitted or
   * zero); consumers must guard against ``undefined`` before constructing a
   * ``Date``.  The "Awaiting Approval" wait timer prefers this over
   * ``startedAt`` so it resets each time an operator acts and the run re-parks.
   */
  queuedAt?: string
  completedAt?: string
  parameters: Record<string, unknown>
  steps: ExecutionStep[]
  totalCost?: number
}

/**
 * Execution list response from GET /v1/automations/{id}/executions or GET /v1/executions.
 *
 * ``next_cursor`` is the value to pass back to the API as ``?cursor=`` to fetch
 * the next page.  The backend currently encodes this as the ``Created``
 * timestamp of the last row on the page (as a numeric string).  Treat the value
 * as opaque: do NOT construct it client-side from row fields, since the
 * encoding is owned by the backend and may change.  Only present when
 * ``has_more`` is true.
 */
export interface ExecutionListResponse {
  data: Execution[]
  has_more: boolean
  next_cursor?: string
  object: 'list'
}

/**
 * Run automation request for POST /v1/automations/{id}/execute.
 */
export interface RunAutomationRequest {
  parameters: Record<string, unknown>
}

/**
 * Run automation response.
 */
export interface RunAutomationResponse {
  execution_id: string
  status: ExecutionStatus
}

/**
 * Abort execution request for PATCH /v1/executions/{id}.
 */
export interface AbortExecutionRequest {
  status: 'aborted'
}

// ── Filter & Sort Types ─────────────────────────────────────────────────────

/**
 * Filter parameters for automation list.
 */
export interface AutomationFilters {
  q?: string // Search query (name)
  trigger?: AutomationTriggerType[] // Trigger type filter
  status?: AutomationStatus[] // Status filter
  last_from?: string // ISO date - last run from
  last_to?: string // ISO date - last run to
}

/**
 * Sort state for automation list.
 */
export interface AutomationSortState {
  column: 'name' | 'lastRunAt' | 'successRate' | 'status' | null
  direction: 'asc' | 'desc' | null
}

/**
 * Filter parameters for execution list.
 */
export interface ExecutionFilters {
  automationId?: string
  status?: ExecutionStatus[]
  from?: string
  to?: string
}

// ── Detail Cache Types ──────────────────────────────────────────────────────

/**
 * Cached automation detail including recent executions.
 */
export interface AutomationDetail {
  automation: Automation
  executions?: Execution[]
  lastFetchedAt: number
}

// ── Inspector Tab Types ─────────────────────────────────────────────────────

export type AutomationInspectorTab = 'config' | 'steps' | 'runs' | 'history'

// ── Saved Filter Presets ────────────────────────────────────────────────────

export interface AutomationFilterPreset {
  id: string
  label: string
  filters: AutomationFilters
  isDefault?: boolean
}

/**
 * Default filter presets per task spec S7.5.
 */
export const DEFAULT_AUTOMATION_PRESETS: AutomationFilterPreset[] = [
  {
    id: 'all',
    label: 'All',
    filters: {},
    isDefault: true,
  },
  {
    id: 'active',
    label: 'Active',
    filters: { status: ['active'] },
  },
  {
    id: 'paused',
    label: 'Paused',
    filters: { status: ['paused'] },
  },
  {
    id: 'drafts',
    label: 'Drafts',
    filters: { status: ['draft'] },
  },
  {
    id: 'failing',
    label: 'Failing today',
    filters: {
      status: ['failing'],
      last_from: new Date().toISOString().split('T')[0],
    },
  },
]

// ── Trigger Type Display Helpers ────────────────────────────────────────────

/**
 * Display label for trigger types.
 */
export const TRIGGER_TYPE_LABELS: Record<AutomationTriggerType, string> = {
  event: 'Event',
  cron: 'Scheduled',
  webhook: 'Webhook',
  manual: 'Manual',
}

/**
 * Color token mapping for trigger types per task spec S6.
 */
export const TRIGGER_TYPE_COLORS: Record<AutomationTriggerType, string> = {
  event: 'var(--purple)',
  cron: 'var(--info)',
  webhook: 'var(--accent-primary)',
  manual: 'var(--text-secondary)',
}

// ── Status Display Helpers ──────────────────────────────────────────────────

/**
 * Display label for automation statuses.
 */
export const AUTOMATION_STATUS_LABELS: Record<AutomationStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  draft: 'Draft',
  failing: 'Failing',
  deprecated: 'Deprecated',
}

/**
 * Color token mapping for automation statuses per task spec S6.
 */
export const AUTOMATION_STATUS_COLORS: Record<AutomationStatus, string> = {
  active: 'var(--success)',
  paused: 'var(--warning)',
  draft: 'var(--text-muted)',
  failing: 'var(--error)',
  deprecated: 'var(--text-muted)',
}

/**
 * Color token mapping for execution step statuses per task spec S6.
 */
export const STEP_STATUS_COLORS: Record<ExecutionStepStatus, string> = {
  succeeded: 'var(--success)',
  running: 'var(--accent-primary)',
  failed: 'var(--error)',
  skipped: 'var(--text-muted)',
  pending: 'var(--text-muted)',
}
